// Slide-over panel showing a single Contact. Phase 3a renders the
// view + delegates editing to ContactForm; phase 3b will lift the
// most-edited fields (displayName, tags, star) into inline cells in
// the grid header & body.
//
// The panel doesn't own the URL — its visibility is driven by an `id`
// prop. Callers (PeopleView / CompaniesView / SlideOverHost) decide
// what id to pass and how closing should affect the URL.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Contact, CustomFieldDef, NewContact } from '@personical/cc-shared-types';
import { addressLines, mapLink } from '@personical/cc-shared-types';
import { useAuth } from '../auth.js';
import { ActivityTimeline } from '../components/ActivityTimeline.js';
import { Avatar } from '../components/Avatar.js';
import { CallTreatmentBadge } from '../components/CallTreatmentBadge.js';
import { ContactForm } from '../components/ContactForm.js';
import { StarButton } from '../components/StarButton.js';
import { useApi } from '../components/api-context.js';
import { displayNameFor } from '../display.js';
import { useToast } from '../toast.js';
import { SlideOver } from './SlideOver.js';

interface ContactPanelProps {
  id: string;
  open: boolean;
  onClose(): void;
  /** Called after the contact is saved/deleted so the parent can refresh. */
  onChange?(contact: Contact | null): void;
}

export function ContactPanel({ id, open, onClose, onChange }: ContactPanelProps) {
  const api = useApi();
  const toast = useToast();
  const { state } = useAuth();
  const currentOid = state.kind === 'authenticated' ? state.user.oid : null;

  const [contact, setContact] = useState<Contact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);

  useEffect(() => {
    if (!open || !id) return;
    let cancelled = false;
    setContact(null);
    setError(null);
    setEditing(false);
    api
      .get(id)
      .then((c) => {
        if (!cancelled) setContact(c);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [id, open, api]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (!cancelled) setCustomFields(s.customFields ?? []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, api]);

  async function handleSave(input: NewContact) {
    if (!contact) return;
    setBusy(true);
    try {
      const updated = await api.update(contact.id, input);
      setContact(updated);
      setEditing(false);
      onChange?.(updated);
      toast.success('Saved');
    } finally {
      setBusy(false);
    }
  }

  async function toggleStar() {
    if (!contact || !currentOid) return;
    const isStarred = (contact.favoritedBy ?? []).includes(currentOid);
    setBusy(true);
    try {
      const updated = await api.setFavorite(contact.id, !isStarred);
      setContact(updated);
      onChange?.(updated);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!contact) return;
    if (!confirm(`Move ${contact.displayName} to trash?`)) return;
    setBusy(true);
    try {
      await api.remove(contact.id);
      onChange?.(null);
      onClose();
      toast.success('Moved to trash');
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const name = contact ? displayNameFor(contact, currentOid) : '';
  const starred = contact && currentOid ? (contact.favoritedBy ?? []).includes(currentOid) : false;

  return (
    <SlideOver open={open} onClose={onClose} ariaLabel={contact ? `${name} details` : 'Contact'}>
      <div className="flex items-center gap-2 border-b border-divider px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-muted hover:surface-hover"
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-faint">
            {contact?.kind === 'company' ? 'Company' : 'Person'}
          </p>
          <p className="text-sm font-semibold text-strong truncate">{name || '…'}</p>
        </div>
        {contact && !editing && (
          <>
            <StarButton filled={starred} onClick={toggleStar} size={18} />
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="btn-secondary px-3 py-1.5 text-sm font-medium"
            >
              Edit
            </button>
          </>
        )}
        {contact && (
          <Link
            to={`/contacts/${contact.id}`}
            className="text-xs text-muted hover:text-strong px-2"
            title="Open full page"
            onClick={onClose}
          >
            ↗
          </Link>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && (
          <p className="m-4 rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200">
            {error}
          </p>
        )}
        {!error && !contact && <p className="p-4 text-sm text-muted">Loading…</p>}
        {contact && editing && (
          <div className="p-4">
            <ContactForm
              initial={toNewContact(contact)}
              submitLabel={busy ? 'Saving…' : 'Save'}
              customFields={customFields}
              api={api}
              excludeId={contact.id}
              onSubmit={handleSave}
              onCancel={() => setEditing(false)}
            />
          </div>
        )}
        {contact && !editing && (
          <ContactBody contact={contact} customFields={customFields} />
        )}
      </div>

      {contact && !editing && (
        <div className="border-t border-divider px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            className="text-sm text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
          >
            Move to trash
          </button>
          <p className="text-xs text-faint">
            Updated {new Date(contact.updatedAt).toLocaleDateString()}
          </p>
        </div>
      )}
    </SlideOver>
  );
}

function ContactBody({
  contact,
  customFields
}: {
  contact: Contact;
  customFields: CustomFieldDef[];
}) {
  return (
    <div className="p-4 space-y-5">
      <div className="flex items-center gap-3">
        <Avatar name={contact.displayName} photoUrl={contact.photoUrl} size="lg" />
        <div className="min-w-0">
          <p className="text-lg font-semibold text-strong truncate">{contact.displayName}</p>
          <div className="flex items-center gap-2 mt-0.5 text-sm text-muted">
            {contact.jobTitle && <span>{contact.jobTitle}</span>}
            {contact.department && contact.jobTitle && <span>·</span>}
            {contact.department && <span>{contact.department}</span>}
            {contact.company && (contact.jobTitle || contact.department) && <span>·</span>}
            {contact.company && <span>{contact.company}</span>}
            {contact.callTreatment && contact.callTreatment !== 'normal' && (
              <CallTreatmentBadge treatment={contact.callTreatment} />
            )}
          </div>
        </div>
      </div>

      <Section label="Email">
        {contact.emails?.length ? (
          <ul className="space-y-1">
            {contact.emails.map((e, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <a
                  href={`mailto:${e.address}`}
                  className="text-sm text-mid hover:text-strong hover:underline"
                >
                  {e.address}
                </a>
                {e.type && e.type !== 'other' && (
                  <span className="text-xs text-faint">{e.type}</span>
                )}
                {e.primary && <span className="text-xs text-faint">primary</span>}
              </li>
            ))}
          </ul>
        ) : (
          <Empty />
        )}
      </Section>

      <Section label="Phone">
        {contact.phoneNumbers?.length ? (
          <ul className="space-y-1">
            {contact.phoneNumbers.map((p, i) => {
              const display = p.e164 ?? p.number;
              return (
                <li key={i} className="flex items-baseline gap-2">
                  <a
                    href={`tel:${display}`}
                    className="text-sm text-mid hover:text-strong hover:underline tabular-nums"
                  >
                    {display}
                  </a>
                  {p.type && p.type !== 'other' && (
                    <span className="text-xs text-faint">{p.type}</span>
                  )}
                  {p.primary && <span className="text-xs text-faint">primary</span>}
                </li>
              );
            })}
          </ul>
        ) : (
          <Empty />
        )}
      </Section>

      {contact.address && addressLines(contact.address).length > 0 && (
        <Section label="Address">
          <address className="text-sm not-italic text-mid">
            {addressLines(contact.address, navigator.language).map((l, i) => (
              <span key={i} className="block">
                {l}
              </span>
            ))}
          </address>
          <a
            href={mapLink(contact.address) ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-1 text-xs text-faint hover:text-strong underline"
          >
            Show on map
          </a>
        </Section>
      )}

      {contact.website && (
        <Section label="Website">
          <a
            href={contact.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-mid hover:text-strong hover:underline truncate block"
          >
            {contact.website}
          </a>
        </Section>
      )}

      {contact.tags?.length > 0 && (
        <Section label="Tags">
          <div className="flex flex-wrap gap-1">
            {contact.tags.map((t) => (
              <span
                key={t}
                className="rounded-md px-1.5 py-0.5 text-[11px] font-medium surface-muted text-mid"
              >
                {t}
              </span>
            ))}
          </div>
        </Section>
      )}

      {contact.notes && (
        <Section label="Notes">
          <p className="text-sm text-mid whitespace-pre-wrap">{contact.notes}</p>
        </Section>
      )}

      {customFields.length > 0 && (
        <Section label="Custom fields">
          <dl className="text-sm space-y-1">
            {customFields.map((cf) => {
              const v = contact.custom?.[cf.id];
              if (!v) return null;
              return (
                <div key={cf.id} className="flex gap-2">
                  <dt className="text-faint min-w-[6rem]">{cf.label}</dt>
                  <dd className="text-mid">{v}</dd>
                </div>
              );
            })}
          </dl>
        </Section>
      )}

      <Section label="Activity">
        <ActivityInline contactId={contact.id} />
      </Section>
    </div>
  );
}

function ActivityInline({ contactId }: { contactId: string }) {
  const api = useApi();
  const { state } = useAuth();
  const currentOid = state.kind === 'authenticated' ? state.user.oid : null;
  return <ActivityTimeline api={api} contactId={contactId} currentOid={currentOid} />;
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-faint mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-faint">—</p>;
}

function toNewContact(c: Contact): NewContact {
  return {
    displayName: c.displayName,
    jobTitle: c.jobTitle ?? '',
    department: c.department ?? '',
    address: c.address ?? {},
    company: c.company ?? '',
    website: c.website ?? '',
    emails: c.emails ?? [],
    phoneNumbers: c.phoneNumbers ?? [],
    notes: c.notes ?? '',
    tags: c.tags ?? [],
    callTreatment: c.callTreatment ?? 'normal',
    visibility: c.visibility,
    kind: c.kind,
    companyId: c.companyId,
    custom: c.custom ?? {}
  };
}
